import type { CollaborationNote } from './notesQueries'

export type NoteAnnotation = {
  type: CollaborationNote['annotations'][number]['type']
  content: string
  text: string
  indices: number[]
  url: string
  displayValue?: string | null
  serviceEntityId?: string | null
}

export type NoteContent = { text: string; annotations: NoteAnnotation[] }

const supportedTypes = new Set([
  'bold',
  'italic',
  'strikethrough',
  'url',
  'mention',
  'heading',
  'bulletedListItem',
  'orderedListItem',
])
const blockTypes = new Set(['heading', 'bulletedListItem', 'orderedListItem'])

/** Whether the editor can rebuild this Note exactly, so an edit loses nothing. */
export function canEditNoteAnnotations(
  text: string,
  annotations: CollaborationNote['annotations'],
): boolean {
  if (!annotations.every(({ type }) => supportedTypes.has(type))) return false

  // Mirrors the backend line rule: one block annotation per whole line.
  const blockLineStarts = new Set<number>()
  for (const { type, indices } of annotations) {
    if (!blockTypes.has(type)) continue
    const [start, end] = indices
    if (
      !(start >= 0 && start < end && end <= text.length) ||
      (start > 0 && text[start - 1] !== '\n') ||
      (end < text.length && text[end] !== '\n') ||
      text.slice(start, end).includes('\n') ||
      blockLineStarts.has(start)
    )
      return false
    blockLineStarts.add(start)
  }
  if (
    annotations.some(
      ({ type, content }) => type === 'mention' && content.includes('\n'),
    )
  )
    return false

  const atomic = annotations.filter(
    ({ type }) => type === 'mention' || type === 'url',
  )
  for (const [index, left] of atomic.entries()) {
    for (const right of atomic.slice(index + 1)) {
      if (
        left.indices[0] < right.indices[1] &&
        right.indices[0] < left.indices[1]
      )
        return false
    }
  }

  return annotations
    .filter(({ type }) => type === 'mention')
    .every((mention) =>
      annotations
        .filter(
          ({ type }) =>
            type === 'bold' || type === 'italic' || type === 'strikethrough',
        )
        .every(
          (mark) =>
            mark.indices[1] <= mention.indices[0] ||
            mark.indices[0] >= mention.indices[1] ||
            (mark.indices[0] <= mention.indices[0] &&
              mark.indices[1] >= mention.indices[1]),
        ),
    )
}
